// client/src/components/donations/PublicDonatePage.jsx
//
// /give/:tenantSlug/:pageSlug — public donation page (Roadmap Phase 5.1).
// One-time or monthly, suggested or custom amount, optional "cover the fees",
// optional dedication, then the card form (UnifiedCheckout: Stripe Connect or
// the organization's Square account). No sign-in.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UnifiedCheckout from '../payment/UnifiedCheckout';
import GoalProgressBar from './GoalProgressBar';
import {
  getPublicDonationPage, startGift, confirmGift, formatMoney, feeCover, errorText,
} from '../../services/donationsApi';
import './Donations.css';

export default function PublicDonatePage() {
  const { tenantSlug, pageSlug } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [frequency, setFrequency] = useState('one_time');
  const [amount, setAmount] = useState(null);
  const [custom, setCustom] = useState('');
  const [coverFees, setCoverFees] = useState(true);
  const [dedicationType, setDedicationType] = useState('none');
  const [dedicationName, setDedicationName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let alive = true;
    getPublicDonationPage(tenantSlug, pageSlug)
      .then((d) => {
        if (!alive) return;
        setData(d);
        const list = d.page.suggestedAmounts || [];
        setAmount(list.length ? list[Math.min(1, list.length - 1)] : null);
      })
      .catch((err) => { if (alive) setLoadError(err?.response?.status === 404 ? 'notfound' : 'error'); });
    return () => { alive = false; };
  }, [tenantSlug, pageSlug]);

  const page = data && data.page;
  const currency = (page && page.currency) || 'usd';
  const giftCents = custom !== '' ? Math.round(Number(custom) * 100) : (amount || 0);
  const cover = page && page.offerCoverFees && coverFees ? feeCover(giftCents, data.coverFees) : 0;
  const totalCents = giftCents + cover;
  const perMonth = frequency === 'monthly' ? t('donations.public.perMonth', '/month') : '';

  const amountError = useMemo(() => {
    if (!page) return '';
    if (!giftCents || Number.isNaN(giftCents)) return t('donations.public.chooseAmount', 'Choose an amount');
    if (giftCents < page.minCents) return t('donations.public.minAmount', 'The smallest gift is {{amount}}', { amount: formatMoney(page.minCents, currency) });
    if (data.maxGiftCents && giftCents > data.maxGiftCents) return t('donations.public.maxAmount', 'The largest online gift is {{amount}}', { amount: formatMoney(data.maxGiftCents, currency) });
    return '';
  }, [page, giftCents, data, currency, t]);

  if (loadError) {
    return (
      <main className="dn-public">
        <div className="dn-shell"><div className="dn-card">
          <p className="dn-error" role="alert">
            {loadError === 'notfound'
              ? t('donations.public.notFound', 'This donation page isn’t available.')
              : t('donations.public.loadError', 'The donation page could not be loaded. Try again.')}
          </p>
        </div></div>
      </main>
    );
  }
  if (!data) return <main className="dn-public"><div className="dn-shell"><div className="dn-card">{t('common.loading', 'Loading…')}</div></div></main>;

  const begin = async (e) => {
    e.preventDefault();
    if (amountError) { setError(amountError); return; }
    setBusy(true); setError('');
    try {
      const r = await startGift(tenantSlug, pageSlug, {
        amountCents: giftCents, frequency, coverFees: page.offerCoverFees && coverFees, name, email,
        dedicationType, dedicationName, emailOptIn,
      });
      setCheckout(r);
    } catch (err) {
      setError(errorText(err, t('donations.public.startError', 'We couldn’t start your gift. Try again.')));
    } finally { setBusy(false); }
  };

  const onPaid = async ({ providerPaymentId, sourceToken }) => {
    setError('');
    try {
      const body = checkout.recurringId
        ? { recurringId: checkout.recurringId }
        : { donationId: checkout.donationId, providerPaymentId, sourceToken };
      const r = await confirmGift(tenantSlug, pageSlug, body);
      setDone({ receiptNumber: r.receiptNumber, totalCents: checkout.totalCents, monthly: checkout.frequency === 'monthly' });
      setCheckout(null);
    } catch (err) {
      setError(errorText(err, t('donations.public.confirmError', 'Your payment could not be confirmed. If you were charged, you’ll get a receipt by email.')));
    }
  };

  const org = data.organization;
  return (
    <main className="dn-public">
      <div className="dn-shell">
        <section className="dn-card">
          <p className="dn-org">{org.name}</p>
          <h1 className="dn-title">{page.title}</h1>
          {page.description && <p className="dn-desc">{page.description}</p>}
        </section>

        <section className="dn-card">
          <GoalProgressBar raisedCents={data.progress.raisedCents} goalCents={page.goalCents} donors={data.progress.donors} currency={currency} />
        </section>

        {done && (
          <section className="dn-card" data-testid="gift-done">
            <p className="dn-ok" role="status">
              <strong>{t('donations.public.thanks', 'Thank you, {{name}}!', { name: name.split(' ')[0] || name })}</strong>{' '}
              {done.monthly
                ? t('donations.public.thanksMonthly', 'Your monthly gift of {{amount}} has started. A receipt is on its way to {{email}}, with a link to stop the monthly gift any time.', { amount: formatMoney(done.totalCents, currency), email })
                : t('donations.public.thanksOnce', 'Your gift of {{amount}} was received. A receipt is on its way to {{email}}.', { amount: formatMoney(done.totalCents, currency), email })}
            </p>
            {page.thankYouMessage && <p className="dn-desc" style={{ marginTop: 12 }}>{page.thankYouMessage}</p>}
            {done.receiptNumber && <p className="dn-note" style={{ marginTop: 8 }}>{t('donations.public.receiptNo', 'Receipt {{number}}', { number: done.receiptNumber })}</p>}
          </section>
        )}

        {!done && !data.payments.available && (
          <section className="dn-card"><p className="dn-note" role="status">{t('donations.public.unavailable', '{{org}} isn’t taking donations online yet.', { org: org.name })}</p></section>
        )}

        {!done && data.payments.available && !checkout && (
          <section className="dn-card">
            <form className="dn-form" onSubmit={begin} noValidate>
              {page.allowMonthly && (
                <div className="dn-seg" role="group" aria-label={t('donations.public.frequency', 'How often')}>
                  <button type="button" aria-pressed={frequency === 'one_time'} onClick={() => setFrequency('one_time')}>{t('donations.public.once', 'Give once')}</button>
                  <button type="button" aria-pressed={frequency === 'monthly'} onClick={() => setFrequency('monthly')}>{t('donations.public.monthly', 'Monthly')}</button>
                </div>
              )}
              {page.suggestedAmounts.length > 0 && (
                <div className="dn-amounts" role="group" aria-label={t('donations.public.amount', 'Amount')}>
                  {page.suggestedAmounts.map((a) => (
                    <button key={a} type="button" className="dn-amount" aria-pressed={custom === '' && amount === a} onClick={() => { setAmount(a); setCustom(''); }}>
                      {formatMoney(a, currency)}
                    </button>
                  ))}
                </div>
              )}
              {page.allowCustom && (
                <label className="dn-field" htmlFor="dn-custom">
                  {t('donations.public.otherAmount', 'Other amount')}
                  <input id="dn-custom" type="number" inputMode="decimal" min={page.minCents / 100} step="1" value={custom}
                    onChange={(e) => setCustom(e.target.value)} placeholder={formatMoney(page.minCents, currency)} />
                </label>
              )}
              {page.offerCoverFees && giftCents > 0 && (
                <label className="dn-check">
                  <input type="checkbox" checked={coverFees} onChange={(e) => setCoverFees(e.target.checked)} />
                  <span>{t('donations.public.coverFees', 'Add {{amount}} to cover processing fees, so {{org}} gets the full gift', { amount: formatMoney(feeCover(giftCents, data.coverFees), currency), org: org.name })}</span>
                </label>
              )}
              {page.allowDedication && (
                <div className="dn-row">
                  <label className="dn-field" htmlFor="dn-ded">
                    {t('donations.public.dedication', 'Dedicate this gift')}
                    <select id="dn-ded" value={dedicationType} onChange={(e) => setDedicationType(e.target.value)}>
                      <option value="none">{t('donations.public.dedNone', 'No dedication')}</option>
                      <option value="honor">{t('donations.public.dedHonor', 'In honor of…')}</option>
                      <option value="memory">{t('donations.public.dedMemory', 'In memory of…')}</option>
                    </select>
                  </label>
                  {dedicationType !== 'none' && (
                    <label className="dn-field" htmlFor="dn-ded-name">
                      {t('donations.public.dedName', 'Name')}
                      <input id="dn-ded-name" value={dedicationName} onChange={(e) => setDedicationName(e.target.value)} required maxLength={200} />
                    </label>
                  )}
                </div>
              )}
              <div className="dn-row">
                <label className="dn-field" htmlFor="dn-name">
                  {t('donations.public.yourName', 'Your name')}
                  <input id="dn-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required maxLength={200} />
                </label>
                <label className="dn-field" htmlFor="dn-email">
                  {t('donations.public.email', 'Email for your receipt')}
                  <input id="dn-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
                </label>
              </div>
              <label className="dn-check">
                <input type="checkbox" checked={emailOptIn} onChange={(e) => setEmailOptIn(e.target.checked)} />
                <span>{t('donations.public.optIn', 'Send me news from {{org}}', { org: org.name })}</span>
              </label>
              {error && <p className="dn-error" role="alert">{error}</p>}
              <div className="dn-summary"><span>{t('donations.public.total', 'Total')}</span><span>{formatMoney(totalCents, currency)}{perMonth}</span></div>
              <button type="submit" className="dn-primary" disabled={busy}>
                {busy ? t('donations.public.starting', 'One moment…') : t('donations.public.continue', 'Continue to payment')}
              </button>
              {org.exemptStatus === '501c3' && (
                <p className="dn-note">{t('donations.public.taxNote', '{{org}} is a 501(c)(3) nonprofit. Your receipt shows what you can deduct.', { org: org.name })}</p>
              )}
            </form>
          </section>
        )}

        {!done && checkout && (
          <section className="dn-card" data-testid="gift-checkout">
            <div className="dn-summary" style={{ marginBottom: 12 }}>
              <span>{frequency === 'monthly' ? t('donations.public.monthlyGift', 'Monthly gift') : t('donations.public.gift', 'Gift')}</span>
              <span>{formatMoney(checkout.totalCents, currency)}{perMonth}</span>
            </div>
            {error && <p className="dn-error" role="alert" style={{ marginBottom: 12 }}>{error}</p>}
            <UnifiedCheckout
              provider={checkout.provider}
              clientSecret={checkout.clientSecret}
              sdkConfig={checkout.sdkConfig}
              amountLabel={`${formatMoney(checkout.totalCents, currency)}${perMonth}`}
              onSuccess={onPaid}
              onError={(msg) => setError(msg)}
              onCancel={() => { setCheckout(null); setError(''); }}
            />
          </section>
        )}

        <p className="dn-foot">{t('donations.public.poweredBy', 'Secure payment. Powered by On-Screen.')}</p>
      </div>
    </main>
  );
}
